import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../api/api';
import { AuthContext } from '../context/AuthContext';
import { AUTH_CONFIG, API_ENDPOINTS } from '../config/constants';
import { getToken } from '../utils/authUtils';

// Import UI components
import GlassCard from '../components/ui/GlassCard';
import { Building2, Mail, Check, ChevronRight, Briefcase, Users } from 'lucide-react';

const EmployerCompleteProfilePage = () => {
    const { updateProfileCompletion, user } = useContext(AuthContext);
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        companyName: '',
        isConsultancy: false,
        employeeCount: '',
        businessEmail: '', // Initialize businessEmail to prevent uncontrolled to controlled warning
        userType: 'employer' // Explicitly set user type
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [step, setStep] = useState(1); // Track multi-step form progress
    const [agreeTerms, setAgreeTerms] = useState(false); // Add missing agreeTerms state
    
    // Pre-populate email from authenticated user
    useEffect(() => {
        if (user && user.email) {
            setFormData(prev => ({
                ...prev,
                businessEmail: user.email // Auto-fill with authenticated email
            }));
        }
    }, [user]);
    
    // Check if user is already on the employer dashboard page
    useEffect(() => {
        // If user already has a completed profile, redirect to dashboard
        if (user && user.profileCompleted === true) {

            navigate('/employer/dashboard', { replace: true });
        }
    }, [user, navigate]);
    
    // Form progress animation variants
    const progressVariants = {
        active: { scale: 1.1, backgroundColor: '#FBD149' },
        inactive: { scale: 1, backgroundColor: '#E5E7EB' },
        completed: { scale: 1, backgroundColor: '#000000' }};

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    // Navigate to next step
    const handleNextStep = () => {
        if (step === 1 && (!formData.name || !formData.companyName)) {
            setError('Please fill in all required fields');
            return;
        }
        if (step === 2 && !formData.employeeCount) {
            setError('Please select your company size');
            return;
        }
        if (step === 3 && !agreeTerms) {
            setError('Please agree to the terms and conditions');
            return;
        }
        
        setError('');
        if (step < 3) {
            setStep(step + 1);
        } else {
            handleSubmit();
        }
    };
    
    // Go back to previous step
    const handlePrevStep = () => {
        if (step > 1) {
            setStep(step - 1);
            setError('');
        }
    };
    
    const handleSubmit = async () => {
        setLoading(true);
        setError('');
        
        try {
            // Get referral code from localStorage if available
            const referralCode = localStorage.getItem('pendingReferralCode');
            
            // Create employer data object - map frontend fields to backend expected fields
            const employerData = {
                name: formData.name,
                companyName: formData.companyName,
                companySize: formData.employeeCount, // Map employeeCount to companySize
                email: formData.businessEmail, // Map businessEmail to email
                isConsultancy: formData.isConsultancy,
                userType: 'employer',
                profileCompleted: true
            };
            
            // Include referral code if available
            if (referralCode) {
                employerData.referralCode = referralCode;
                console.log('📌 Including referral code in profile completion:', referralCode);
            }
            
            // Get current token
            const token = getToken();
            if (!token) {
                throw new Error('No authentication token found. Please log in again.');
            }
            
            // Make API call to persist profile data to backend
            const response = await api.post(
                API_ENDPOINTS.AUTH.COMPLETE_PROFILE,
                employerData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            // Extract updated user and token from response
            const responseData = response.data.data || response.data;
            const { user: updatedUser, token: newToken } = responseData;

            // Store new token FIRST before updating context
            if (newToken) {
                localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, newToken);
            }
            
            // Clear the referral code from localStorage after successful submission
            if (referralCode) {
                localStorage.removeItem('pendingReferralCode');
                console.log('✅ Referral code processed and cleared from localStorage');
            }
            
            // Update profile completion in AuthContext with server response
            if (typeof updateProfileCompletion === 'function') {
                updateProfileCompletion(true, updatedUser);
            } else {
                console.warn('AuthContext updateProfileCompletion function not available');
            }
            
            // Force page reload to refresh all data with new token
            setTimeout(() => {
                window.location.href = '/employer/dashboard';
            }, 1000);
            
            // Show success message
            setSuccess('Profile completed successfully! Redirecting to dashboard...');
            
            // Navigate to dashboard
            setTimeout(() => {
                navigate('/employer/dashboard', { replace: true });
            }, 1000);
            
        } catch (err) {
            console.error('Profile completion error:', err);
            
            // Handle different error scenarios
            if (err.response) {
                if (err.response.status === 401) {
                    setError('Your session has expired. Please log in again.');
                    setTimeout(() => navigate('/employer/auth'), 2000);
                } else if (err.response.status === 400) {
                    setError(err.response.data?.message || 'Invalid profile data. Please check your inputs.');
                } else if (err.response.status === 404) {
                    setError('User account not found. Please log in again.');
                } else {
                    setError('Failed to save profile. Please try again.');
                }
            } else if (err.request) {
                setError('Network error. Please check your connection and try again.');
            } else {
                setError(err.message || 'Something went wrong. Please try again.');
            }
            
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 text-black flex items-center justify-center font-sans relative overflow-hidden py-10">
            {/* Background with subtle pattern */}
            <div className="absolute inset-0 bg-white opacity-90 z-0"></div>
            
            <GlassCard variant="light" className="w-full max-w-2xl mx-4 relative z-10 bg-white shadow-lg">
                {/* Page title to clearly indicate this is for employers */}
                <div className="text-center mb-4">
                    <h1 className="text-2xl font-bold">Employer Profile Setup</h1>
                </div>
                
                {/* Progress indicator */}
                <div className="flex justify-center mb-8">
                    <div className="flex items-center">
                        {[1, 2, 3].map((stepNumber) => (
                            <React.Fragment key={stepNumber}>
                                <motion.div 
                                    className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium text-sm"
                                    variants={progressVariants}
                                    animate={step > stepNumber ? 'completed' : step === stepNumber ? 'active' : 'inactive'}
                                >
                                    {step > stepNumber ? <Check size={16} /> : stepNumber}
                                </motion.div>
                                {stepNumber < 3 && (
                                    <div className={`w-12 h-1 ${step > stepNumber ? 'bg-black' : 'bg-gray-300'}`}></div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
                
                <h2 className="text-3xl font-bold mb-2 text-black">
                    {step === 1 && "Let's get you started!"}
                    {step === 2 && "Tell us about your company"}
                    {step === 3 && "Almost there!"}
                </h2>
                <p className="text-gray-600 mb-8">
                    {step === 1 && "Complete your employer profile to access the dashboard"}
                    {step === 2 && "Help us understand your hiring needs better"}
                    {step === 3 && "Just a few more details before you're ready"}
                </p>
                
                <div className="space-y-6">
                    {/* Step 1: Basic Info */}
                    {step === 1 && (
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }} 
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                            className="space-y-6"
                        >
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Your full name</label>
                                <div className="relative">
                                    <input 
                                        id="name" 
                                        name="name" 
                                        type="text" 
                                        required 
                                        value={formData.name} 
                                        onChange={handleChange} 
                                        className="w-full p-4 pl-12 bg-white border-b-2 border-black focus:border-yellow-400 focus:outline-none transition-all" 
                                    />
                                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600">
                                        <Users size={18} />
                                    </div>
                                </div>
                            </div>
                            
                            <div>
                                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">Company name</label>
                                <div className="relative">
                                    <input 
                                        id="companyName" 
                                        name="companyName" 
                                        type="text" 
                                        required 
                                        value={formData.companyName} 
                                        onChange={handleChange} 
                                        className="w-full p-4 pl-12 bg-white border-b-2 border-black focus:border-yellow-400 focus:outline-none transition-all" 
                                    />
                                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600">
                                        <Building2 size={18} />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex items-center p-4 border-b border-gray-200">
                                <input 
                                    id="isConsultancy" 
                                    name="isConsultancy" 
                                    type="checkbox" 
                                    checked={formData.isConsultancy} 
                                    onChange={handleChange} 
                                    className="h-5 w-5 rounded-md border-gray-300 text-black focus:ring-yellow-400 focus:ring-offset-0" 
                                />
                                <label htmlFor="isConsultancy" className="ml-3 block text-sm text-gray-700">
                                    This is a consultancy (Hiring or staffing agency)
                                </label>
                            </div>
                        </motion.div>
                    )}
                    
                    {/* Step 2: Company Details */}
                    {step === 2 && (
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }} 
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                            className="space-y-6"
                        >
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-4">Number of employees in your company</label>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {['0-50', '51-100', '101-300', '301-500', '501-1000', '1000+'].map(size => (
                                        <div 
                                            key={size} 
                                            className={`p-4 text-center cursor-pointer transition-all ${formData.employeeCount === size 
                                                ? 'bg-black text-white border-2 border-black' 
                                                : 'bg-white text-black border border-gray-300'}`}
                                            onClick={() => setFormData({...formData, employeeCount: size})}
                                        >
                                            <div className="flex flex-col items-center justify-center gap-2">
                                                <Users size={18} className={formData.employeeCount === size ? 'text-white' : 'text-gray-600'} />
                                                <span className="text-sm font-medium">{size}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            <div>
                                <label htmlFor="businessEmail" className="block text-sm font-medium text-gray-700 mb-2">
                                    Work Email <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input 
                                        id="businessEmail" 
                                        name="businessEmail" 
                                        type="email"
                                        required
                                        placeholder="your.email@company.com"
                                        value={formData.businessEmail} 
                                        onChange={handleChange} 
                                        className="w-full p-4 pl-12 bg-white border-b-2 border-black focus:border-yellow-400 focus:outline-none transition-all" 
                                    />
                                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600">
                                        <Mail size={18} />
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">We'll use this for important notifications</p>
                            </div>
                        </motion.div>
                    )}
                    
                    {/* Step 3: Final Step */}
                    {step === 3 && (
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }} 
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                            className="space-y-6"
                        >
                            <div className="p-6 bg-white border border-gray-200">
                                <div className="space-y-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-gray-100 rounded-full">
                                            <Users size={20} className="text-black" />
                                        </div>
                                        <div>
                                            <h3 className="font-medium text-black">Personal Details</h3>
                                            <p className="text-sm text-gray-600">{formData.name}</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-gray-100 rounded-full">
                                            <Building2 size={20} className="text-black" />
                                        </div>
                                        <div>
                                            <h3 className="font-medium text-black">Company Details</h3>
                                            <p className="text-sm text-gray-600">{formData.companyName} {formData.isConsultancy && '(Consultancy)'}</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-gray-100 rounded-full">
                                            <Briefcase size={20} className="text-black" />
                                        </div>
                                        <div>
                                            <h3 className="font-medium text-black">Company Size</h3>
                                            <p className="text-sm text-gray-600">{formData.employeeCount} employees</p>
                                        </div>
                                    </div>
                                    
                                    {formData.businessEmail && (
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-gray-100 rounded-full">
                                                <Mail size={20} className="text-black" />
                                            </div>
                                            <div>
                                                <h3 className="font-medium text-black">Work Email</h3>
                                                <p className="text-sm text-gray-600">{formData.businessEmail}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            <div className="flex items-center p-4 border-b border-gray-200">
                                <input 
                                    id="agree" 
                                    type="checkbox" 
                                    checked={agreeTerms}
                                    onChange={(e) => setAgreeTerms(e.target.checked)}
                                    className="h-5 w-5 rounded-md border-gray-300 text-black focus:ring-yellow-400 focus:ring-offset-0" 
                                />
                                <label htmlFor="agree" className="ml-3 block text-sm text-gray-700">
                                    I agree to RecruiterAI's <Link to="/terms" target="_blank" className="text-black font-medium border-b border-black hover:text-gray-700">Terms & Conditions</Link> and <Link to="/privacy" target="_blank" className="text-black font-medium border-b border-black hover:text-gray-700">Privacy Policy</Link>
                                </label>
                            </div>
                        </motion.div>
                    )}
                    
                    {/* Error Message */}
                    {error && (
                        <motion.div 
                            initial={{ opacity: 0, y: 10 }} 
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <div className="bg-white border-l-4 border-red-500 text-red-600 p-4 flex items-start gap-3">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <p className="text-sm">{error}</p>
                            </div>
                        </motion.div>
                    )}
                    
                    {/* Success Message */}
                    {success && (
                        <motion.div 
                            initial={{ opacity: 0, y: 10 }} 
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <div className="bg-white border-l-4 border-green-500 text-green-600 p-4 flex items-start gap-3">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="text-sm">{success}</p>
                            </div>
                        </motion.div>
                    )}
                    
                    {/* Navigation Buttons */}
                    <div className="flex justify-between pt-4">
                        {step > 1 ? (
                            <button 
                                onClick={handlePrevStep}
                                className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-100 transition-colors"
                            >
                                Back
                            </button>
                        ) : <div></div>}
                        
                        <button 
                            onClick={handleNextStep}
                            disabled={loading}
                            className="px-6 py-3 font-bold bg-black text-white border-2 border-black hover:bg-gray-900 transition-colors flex items-center gap-2"
                        >
                            {loading ? (
                                <div className="flex items-center gap-2">
                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Processing...
                                </div>
                            ) : (
                                <>
                                    {step === 3 ? 'Complete Employer Profile' : 'Continue'}
                                    {step !== 3 && <ChevronRight size={16} />}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </GlassCard>
        </div>
    );
};

export default EmployerCompleteProfilePage;
